import axios from 'axios'
import { BASE_URL, USE_MOCK } from './config'
import { clearSession, hasValidSession, session } from '@/store/session'
import router from '@/router'

// axios 实例
const http = axios.create({
  baseURL: BASE_URL,
  timeout: 15000
})

http.interceptors.request.use(function (cfg) {
  const hadToken = !!session.token
  const valid = hasValidSession()
  if (hadToken && !valid) {
    if (router.currentRoute.path !== '/login') {
      router.replace({ path: '/login', query: { redirect: router.currentRoute.fullPath } })
    }
    return Promise.reject(new Error('登录已过期，请重新登录'))
  }
  if (valid && session.token) {
    cfg.headers.Authorization = 'Bearer ' + session.token
  }
  return cfg
})

// 统一解包：兼容若依 AjaxResult({code,msg,data}) 或直接返回业务对象
http.interceptors.response.use(
  function (res) {
    const d = res && res.data
    // 若依风格
    if (d && Object.prototype.hasOwnProperty.call(d, 'code')) {
      if (d.code === 200 || d.code === 0) return d.data
      const msg = d.msg || d.message || '请求失败'
      return Promise.reject(new Error(msg))
    }
    // 直接业务数据
    return d
  },
  function (err) {
    const status = err && err.response && err.response.status
    const responseMessage = err && err.response && err.response.data &&
      (err.response.data.msg || err.response.data.message)
    if (status === 401) {
      clearSession()
      if (router.currentRoute.path !== '/login') {
        router.replace({ path: '/login', query: { redirect: router.currentRoute.fullPath } })
      }
      return Promise.reject(new Error('登录已过期，请重新登录'))
    }
    if (status === 403 && responseMessage === '账号已被禁用') {
      clearSession()
      if (router.currentRoute.path !== '/login') {
        router.replace({ path: '/login', query: { disabled: '1' } })
      }
      return Promise.reject(new Error(responseMessage))
    }
    const msg =
      responseMessage ||
      (err && err.message) ||
      '网络错误'
    return Promise.reject(new Error(msg))
  }
)
function normalizeAuth (resp) {
  if (!resp) return resp
  if (resp.user) return resp
  // 扁平 → 包成 user
  const user = {
    userId: resp.userId,
    steamId: resp.steamId,
    username: resp.username,
    nickname: resp.username || ('幸存者-' + (resp.steamId || '').slice(-4))
  }
  return { ...resp, user }
}

// ===== 真实后端 =====
const realApi = {
  // 登录（已存在用户直接返回 token；新用户返回 isNew/needPaySecret）
  authLogin (steamId, username) {
    return http.post('/api/auth/steam-login', { steamId, username }).then(d => normalizeAuth(d))
  },
  // 首次设置支付口令并登录
  authSetPaySecret (steamId, passphrase, username) {
    return http.post('/api/auth/set-pay-secret', { steamId, passphrase, username }).then(d => normalizeAuth(d))
  },
  // 兼容旧调用名
  authRegister (steamId, passphrase, username) {
    return http.post('/api/auth/set-pay-secret', { steamId, passphrase, username })
  },
  // 解锁后获取购买记录（后端校验口令）
  getPurchases (passphrase) {
    // 返回示例：[{ time, serverId, name, price, cdk }]
    return http.post('/api/orders/me', { passphrase })
  },
  getServers (params = {}) {
    return http.get('/api/servers', { params })// 返回 { total, items }
  },
  getCategoriesByServer (serverCode, params = {}) {
    return http.get(`/api/servers/${serverCode}/categories`, { params }).then(r => r.data || r)
  },
  getGoodsByServer (serverCode, params = {}) {
    return http.get(`/api/servers/${serverCode}/goods`, { params }).then(r => r.data || r)
  },
  purchase (serverCode, goodsId, passphrase, currency) {
    const body = { goodsId, passphrase }
    if (currency) body.currency = currency
    return http.post(`/api/servers/${serverCode}/purchase`, body).then(r => r.data || r)
  },
  getWalletBalance (serverCode) {
    return http.get(`/api/wallet/${serverCode}`).then(r => r.data || r)
  },
  getSiteConfig () {
    return http.get('/api/site/config')
  },
  getMyOrders (passphrase, { page = 1, size = 20, serverCode } = {}) {
    return http.post('/api/orders/me', { passphrase, page, size, serverCode }).then(r => r.data || r)
  },
  getMyInsuranceOrders (passphrase, { page = 1, size = 20, serverCode } = {}) {
    return http.post('/api/orders/me/insurance', { passphrase, page, size, serverCode }).then(r => r.data || r)
  },
  getSiteAnnouncement () {
    return http.get('/api/site/announcement')
  },
  applyVehicle (orderId, extra = {}) {
    return http.post(`/api/orders/${orderId}/apply-vehicle`, extra).then(r => r.data || r)
  }
}

// ===== Mock（可选）=====
function delay (x, ms = 300) {
  return new Promise(function (resolve) {
    setTimeout(function () { resolve(x) }, ms)
  })
}

const mockDb = { users: {} }
function token () {
  return Math.random().toString(36).slice(2) + '.' + Math.random().toString(36).slice(2)
}
let uidSeq = 1

const mockApi = {
  authLogin (steamId, username) {
    const u = mockDb.users[steamId]
    if (!u) {
      // 新用户：提示需要设置口令
      return delay({
        isNew: true,
        needPaySecret: true,
        token: null,
        user: { userId: null, steamId, nickname: username || ('幸存者-' + (steamId || '0000').slice(-4)) }
      })
    }
    // 老用户：直接登录
    u.token = token()
    return delay({
      isNew: false,
      needPaySecret: false,
      token: u.token,
      user: { userId: u.userId, steamId, nickname: u.nickname }
    })
  },

  authSetPaySecret (steamId, passphrase, username) {
    let u = mockDb.users[steamId]
    if (!u) {
      u = { userId: uidSeq++, nickname: username || ('幸存者-' + (steamId || '0000').slice(-4)) }
      mockDb.users[steamId] = u
    }
    u.passphrase = passphrase
    u.token = token()
    return delay({
      token: u.token,
      user: { userId: u.userId, steamId, nickname: u.nickname }
    })
  },

  // 兼容旧名
  authRegister (steamId, passphrase, username) {
    return this.authSetPaySecret(steamId, passphrase, username)
  },

  getPurchases (passphrase) {
    return new Promise(function (resolve, reject) {
      if (!passphrase || passphrase.length < 4) {
        reject(new Error('支付口令不正确'))
        return
      }
      setTimeout(function () {
        resolve([
          { time: new Date().toISOString(), serverId: 'syberia', name: '武器物品 1', price: 80, cdk: 'DZ-ABCD-EFGH' },
          { time: new Date(Date.now() - 3600 * 1000).toISOString(), serverId: 'chernarus', name: '医疗物品 2', price: 40, cdk: 'DZ-IJKL-MNOP' }
        ])
      }, 450)
    })
  }
}

export default (USE_MOCK ? mockApi : realApi)
